import {
  TColumnType as TColumnTypeAntd,
  TPageSizeOptions,
  TTableComponentProps,
} from '@idaho-aeyc/ui-kit';
import {
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/es/table/interface';
import { Dispatch, Key } from 'react';
import { TSortParams } from 'data/types/generic.types';

export type { TColumnFilterItem } from '@idaho-aeyc/ui-kit';

export type TColumnType<T> = Omit<TColumnTypeAntd<T>, 'key'> & {
  key: Key;
  /** Plain-text label for the column picker when title is not a string */
  titleLabel?: string;
};

export type TableProps<T> = Omit<
  TTableComponentProps<T>,
  'onDragEnd' | 'filterColumnContent' | 'columns'
> & {
  tableKey?: string;
  pageHeaderContent?: number;
  storagePath: string;
  columns?: TColumnType<T>[];
  tabHeight?: number;
  headerActions?: React.ReactElement[] | React.ReactElement;
  dataTableName?: string;
};

export type TUseTableParams<T> = {
  storagePath: string;
  defaultSortColumn?: TSortParams;
  defaultPageSize?: TPageSizeOptions;
  defaultColumns: TColumnType<T>[];
  defaultFilters?: Record<string, FilterValue | null>;
};

export type TSelectedRows<T> = {
  selectedRowsKeys: Key[];
  selectedRowsRecords: T[];
};

export type TUseTableReturnData<T> = {
  columns: TColumnType<T>[];
  setColumns: React.Dispatch<React.SetStateAction<TColumnType<T>[]>>;
  page: number;
  pageSize: number;
  sortColumn: TSortParams;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setSortColumn: React.Dispatch<React.SetStateAction<TSortParams>>;
  handleTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[],
  ) => void;
  selectedFilters: Record<string, FilterValue | null>;
  setSelectedFilters: React.Dispatch<
    React.SetStateAction<Record<string, FilterValue | null>>
  >;
  hasSelectedFilter: boolean;
  selectedRows: TSelectedRows<T>;
  setSelectedRows: Dispatch<React.SetStateAction<TSelectedRows<T>>>;
  hasFilters: boolean;
  onFiltersReset: () => void;
};

export type TStorageFilters<T> = {
  selectedFilters?: Record<string, FilterValue | null>;
  sortColumn?: TSortParams;
  page?: number;
  pageSize?: number;
  selectedRows?: TSelectedRows<T>;
};

export enum ETableStoragePaths {
  WORKSHOPS = 'workshops',
  ORGANIZATION = 'organization',
  SESSION_REGISTRANTS = 'sessionRegistrants',
  PEOPLE = 'people',
  TEAM = 'team',
  KIT_MANAGEMENT_WORKSHOP = 'kitManagementWorkshop',
  KIT_MANAGEMENT_PARENT = 'kitManagementParent',
  SUBSCRIBERS_NOTIFY_ME = 'subscribersNotifyMe',
  SUBSCRIBERS_WAIT_LIST = 'subscribersWaitList',
  WORKSHOP_WAIT_LIST = 'workshopWaitList',
  ATTENDANCE_LIST = 'attendanceList',
  WORKSHOP_REGISTRANTS = 'workshopRegistrants',
  FAMILY = 'family',
  WORKSHOP_CHILDCARE = 'workshopChildcare',
  WORKSHOP_TASKS = 'workshopTasks',
  CONTACTS = 'contacts',
  MEETINGS = 'meetings',
  ELC_PLACES = 'elcPlaces',
  ELAC_MEMBERS = 'elacMembers',
  MEETING_ACTION_ITEMS = 'meetingActionItems',
  TEAM_ADMINS = 'teamAdmins',
  TEAM_USERS = 'teamUsers',
  TACTIC = 'tactic',
  TASK = 'task',
  EVENTS = 'events',
  KIT_MANAGEMENT_EVENT = 'kitManagementEvent',
  EVENT_CHILDCARE = 'eventChildcare',
  EVENT_REGISTRANTS = 'eventRegistrants',
  EVENT_WAIT_LIST = 'eventWaitList',
  EVENT_FAMILY = 'eventFamily',
  DUPLICATE_PAIRS_PEOPLE = 'duplicatePairsPeople',
  DUPLICATE_PAIRS_ORGANIZATION = 'duplicatePairsOrganization',
  DUPLICATE_MERGE_HISTORY = 'duplicateMergeHistory',
}

export type TTableStoragePaths = ETableStoragePaths & string;

export type TColumnsState = {
  key: Key;
  show: boolean;
  title: string;
};
